import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import {jsPDF} from 'jspdf';
import 'jspdf-autotable';
import './SalesHistory.css';
import TestPDF from './TestPDF';
import autoTable from 'jspdf-autotable';

const SalesHistory = () => {
  const { user } = useSelector((state) => state.auth);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');

  useEffect(() => {
    fetchSales();
    // eslint-disable-next-line
  }, [startDate, endDate, paymentMethod]);

  const fetchSales = async () => {
    try {
      let url = '/sales/?';
      if (startDate) url += `start_date=${startDate}&`;
      if (endDate) url += `end_date=${endDate}&`;
      if (paymentMethod) url += `payment_method=${paymentMethod}&`;
      
      const response = await api.get(url);
      setSales(response.data);
    } catch (err) {
      console.error('Error fetching sales:', err);
    } finally {
      setLoading(false);
    }
  };

  const viewDetails = async (saleId) => {
    try {
      const response = await api.get(`/sales/${saleId}/`);
      setSelectedSale(response.data);
      setShowDetails(true);
    } catch (err) {
      console.error('Error fetching sale details:', err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Add header
    doc.setFontSize(18);
    doc.setTextColor(27, 94, 76); // #1B5E4C
    doc.text('FeedsHub POS - Sales History', 14, 20);
    
    // Add date range if filtered
    doc.setFontSize(10);
    doc.setTextColor(100);
    let yPos = 30;
    
    if (startDate || endDate) {
      const dateRangeText = `Date Range: ${startDate || 'All'} to ${endDate || 'All'}`;
      doc.text(dateRangeText, 14, yPos);
      yPos += 6;
    }
    
    if (paymentMethod) {
      doc.text(`Payment Method: ${paymentMethod}`, 14, yPos);
      yPos += 6;
    }
    
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, yPos);
    doc.text(`Generated by: ${user?.username}`, 14, yPos + 6);
    
    // Calculate totals
    const totalSales = sales.length;
    const totalRevenue = sales.reduce((sum, sale) => sum + parseFloat(sale.total), 0);
    
    // Add summary
    yPos += 15;
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text(`Total Sales: ${totalSales}`, 14, yPos);
    doc.text(`Total Revenue: KSh ${totalRevenue.toFixed(2)}`, 14, yPos + 7);
    
    // Prepare table data
    const tableData = sales.map(sale => [
      sale.invoice_number,
      new Date(sale.created_at).toLocaleDateString(),
      sale.cashier_name,
      sale.customer_name || 'Walk-in',
      sale.payment_method,
      `KSh ${parseFloat(sale.total).toFixed(2)}`,
      sale.status
    ]);
    
    // Add table
    autoTable(doc, {
      startY: yPos + 15,
      head: [['Invoice #', 'Date', 'Cashier', 'Customer', 'Payment', 'Total', 'Status']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [27, 94, 76], // #1B5E4C
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold'
      },
      styles: {
        fontSize: 9,
        cellPadding: 3
      },
      alternateRowStyles: {
        fillColor: [245, 241, 232] // #F5F1E8
      },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 25 },
        2: { cellWidth: 25 },
        3: { cellWidth: 25 },
        4: { cellWidth: 20 },
        5: { cellWidth: 25, halign: 'right' },
        6: { cellWidth: 20 }
      }
    });
    
    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }
    
    // Save the PDF
    const fileName = `sales_history_${startDate || 'all'}_${endDate || 'all'}.pdf`;
    doc.save(fileName);
  };

  const exportSingleReceiptToPDF = async (saleId) => {
    try {
      // Fetch full sale details if not already loaded
      const response = await api.get(`/sales/${saleId}/`);
      const sale = response.data;
      
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(20);
      doc.setTextColor(27, 94, 76);
      doc.text('FeedsHub POS', 105, 20, { align: 'center' });
      
      doc.setFontSize(16);
      doc.text('SALES RECEIPT', 105, 30, { align: 'center' });
      
      // Receipt details
      doc.setFontSize(10);
      doc.setTextColor(0);
      let yPos = 45;
      
      doc.text(`Invoice Number: ${sale.invoice_number}`, 14, yPos);
      doc.text(`Date: ${formatDate(sale.created_at)}`, 14, yPos + 6);
      doc.text(`Cashier: ${sale.cashier_name}`, 14, yPos + 12);
      doc.text(`Customer: ${sale.customer_name || 'Walk-in'}`, 14, yPos + 18);
      doc.text(`Payment Method: ${sale.payment_method}`, 14, yPos + 24);
      
      // Items table
      yPos += 35;
      const itemsData = sale.items.map(item => [
        item.product_name,
        item.quantity.toString(),
        `KSh ${parseFloat(item.unit_price).toFixed(2)}`,
        `${item.discount_percent}%`,
        `KSh ${parseFloat(item.subtotal).toFixed(2)}`
      ]);
      
      autoTable(doc, {
        startY: yPos,
        head: [['Product', 'Qty', 'Price', 'Discount', 'Subtotal']],
        body: itemsData,
        theme: 'striped',
        headStyles: {
          fillColor: [27, 94, 76],
          textColor: 255,
          fontSize: 10,
          fontStyle: 'bold'
        },
        styles: {
          fontSize: 9,
          cellPadding: 3
        },
        columnStyles: {
          1: { halign: 'center' },
          2: { halign: 'right' },
          3: { halign: 'center' },
          4: { halign: 'right' }
        }
      });
      
      // Totals
      yPos = doc.lastAutoTable.finalY + 10;
      const rightAlign = 180;
      
      doc.setFontSize(10);
      doc.text('Subtotal:', 140, yPos);
      doc.text(`KSh ${parseFloat(sale.subtotal).toFixed(2)}`, rightAlign, yPos, { align: 'right' });
      
      doc.text('Discount:', 140, yPos + 6);
      doc.text(`-KSh ${parseFloat(sale.discount_amount).toFixed(2)}`, rightAlign, yPos + 6, { align: 'right' });
      
      doc.text('Tax:', 140, yPos + 12);
      doc.text(`+KSh ${parseFloat(sale.tax_amount).toFixed(2)}`, rightAlign, yPos + 12, { align: 'right' });
      
      // Draw line
      doc.setLineWidth(0.5);
      doc.line(140, yPos + 16, rightAlign, yPos + 16);
      
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('TOTAL:', 140, yPos + 22);
      doc.text(`KSh ${parseFloat(sale.total).toFixed(2)}`, rightAlign, yPos + 22, { align: 'right' });
      
      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);
      doc.text('Amount Paid:', 140, yPos + 30);
      doc.text(`KSh ${parseFloat(sale.amount_paid).toFixed(2)}`, rightAlign, yPos + 30, { align: 'right' });
      
      doc.text('Change:', 140, yPos + 36);
      doc.text(`KSh ${parseFloat(sale.change_amount).toFixed(2)}`, rightAlign, yPos + 36, { align: 'right' });
      
      // Footer
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text('Thank you for your business!', 105, yPos + 50, { align: 'center' });
      doc.text('FeedsHub POS System', 105, yPos + 56, { align: 'center' });
      
      // Save
      doc.save(`receipt_${sale.invoice_number}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="sales-history-container">
      <PageHeader title="Sales History" subtitle="View and manage past transactions">
        <button onClick={exportToPDF} className="btn-secondary" disabled={sales.length === 0}>
          📄 Export to PDF
        </button>
        <Link to="/sales" className="btn-primary">
          + New Sale
        </Link>
      </PageHeader>

      <div className="filters-section">
        <div className="filter-group">
          <label>Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>Payment Method</label>
          <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
            <option value="">All Methods</option>
            <option value="cash">Cash</option>
            <option value="card">Card</option>
            <option value="mobile">M-Pesa</option>
          </select>
        </div>
        {(startDate || endDate || paymentMethod) && (
          <button
            onClick={() => {
              setStartDate('');
              setEndDate('');
              setPaymentMethod('');
            }}
            className="btn-clear-filters"
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="sales-table">
        <table>
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Date</th>
              <th>Cashier</th>
              <th>Customer</th>
              <th>Items</th>
              <th>Payment Method</th>
              <th>Total</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sales.map((sale) => (
              <tr key={sale.id}>
                <td><strong>{sale.invoice_number}</strong></td>
                <td>{formatDate(sale.created_at)}</td>
                <td>{sale.cashier_name}</td>
                <td>{sale.customer_name || '-'}</td>
                <td>{sale.items_count}</td>
                <td>
                  <span className={`payment-badge payment-${sale.payment_method}`}>
                    {sale.payment_method}
                  </span>
                </td>
                <td><strong>ksh {parseFloat(sale.total).toFixed(2)}</strong></td>
                <td>
                  <span className={`status-badge status-${sale.status}`}>
                    {sale.status}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button onClick={() => viewDetails(sale.id)} className="btn-view">
                      View
                    </button>
                    <button 
                      onClick={() => exportSingleReceiptToPDF(sale.id)} 
                      className="btn-pdf"
                      title="Export Receipt as PDF"
                    >
                      📄
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sales.length === 0 && (
        <div className="no-sales">
          <p>No sales found</p>
        </div>
      )}

      {showDetails && selectedSale && (
        <div className="modal-overlay" onClick={() => setShowDetails(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Sale Details - {selectedSale.invoice_number}</h3>
              <button onClick={() => setShowDetails(false)} className="close-btn">
                ×
              </button>
            </div>

            <div className="sale-details">
              <div className="details-row">
                <div className="detail-item">
                  <strong>Date:</strong>
                  <span>{formatDate(selectedSale.created_at)}</span>
                </div>
                <div className="detail-item">
                  <strong>Cashier:</strong>
                  <span>{selectedSale.cashier_name}</span>
                </div>
                <div className="detail-item">
                  <strong>Customer:</strong>
                  <span>{selectedSale.customer_name || 'Walk-in'}</span>
                </div>
                <div className="detail-item">
                  <strong>Payment:</strong>
                  <span className={`payment-badge payment-${selectedSale.payment_method}`}>
                    {selectedSale.payment_method}
                  </span>
                </div>
              </div>

              <h4>Items</h4>
              <table className="items-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>SKU</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Discount</th>
                    <th>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedSale.items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.product_name}</td>
                      <td>{item.product_sku}</td>
                      <td>{item.quantity}</td>
                      <td>ksh {parseFloat(item.unit_price).toFixed(2)}</td>
                      <td>{item.discount_percent}%</td>
                      <td>ksh {parseFloat(item.subtotal).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="totals-section">
                <div className="total-row">
                  <span>Subtotal:</span>
                  <span>ksh {parseFloat(selectedSale.subtotal).toFixed(2)}</span>
                </div>
                <div className="total-row">
                  <span>Discount:</span>
                  <span className="negative">-ksh {parseFloat(selectedSale.discount_amount).toFixed(2)}</span>
                </div>
                <div className="total-row">
                  <span>Tax:</span>
                  <span>+ksh {parseFloat(selectedSale.tax_amount).toFixed(2)}</span>
                </div>
                <div className="total-row grand-total">
                  <strong>Total:</strong>
                  <strong>ksh {parseFloat(selectedSale.total).toFixed(2)}</strong>
                </div>
                <div className="total-row">
                  <span>Amount Paid:</span>
                  <span>ksh {parseFloat(selectedSale.amount_paid).toFixed(2)}</span>
                </div>
                <div className="total-row">
                  <span>Change:</span>
                  <span>ksh {parseFloat(selectedSale.change_amount).toFixed(2)}</span>
                </div>
              </div>

              {selectedSale.notes && (
                <div className="notes-section">
                  <strong>Notes:</strong>
                  <p>{selectedSale.notes}</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button onClick={() => setShowDetails(false)} className="btn-secondary">
                Close
              </button>
              <button 
                onClick={() => exportSingleReceiptToPDF(selectedSale.id)}
                className="btn-primary"
              >
                📄 Export as PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesHistory;